import { Box, Container, Image, Input, Text } from "@chakra-ui/react"
import {
  Link as RouterLink,
  createFileRoute,
  redirect,
  useRouter,
} from "@tanstack/react-router"
import { type SubmitHandler, useForm } from "react-hook-form"

interface AccessToken {
  username: string
  password: string
  grant_type?: string
}
import { Button } from "@/components/atoms/Button"
import { PublicHeader } from "@/components/layout/PublicHeader"
import { useAuthActions } from "@/hooks/api/useAuthActions"
import { useAuthStore } from "@/stores/auth"
import Logo from "/assets/images/dory-logo.svg"
import { emailPattern, passwordRules } from "../utils"

export const Route = createFileRoute("/login")({
  component: Login,
  beforeLoad: async () => {
    const { isAuthenticated, accessToken } = useAuthStore.getState()
    // Only redirect if we have both authentication state AND a valid token
    if (isAuthenticated && accessToken) {
      throw redirect({
        to: "/",
      })
    }
  },
})

function Login() {
  const { login, isLoading, error } = useAuthActions()
  const router = useRouter()
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<AccessToken>({
    mode: "onBlur",
    criteriaMode: "all",
    defaultValues: {
      username: "",
      password: "",
    },
  })

  const onSubmit: SubmitHandler<AccessToken> = async (data) => {
    if (isSubmitting || isLoading) return

    try {
      await login.mutateAsync({
        email: data.username,
        password: data.password,
      })
      // Redirect to dashboard after successful login
      router.navigate({ to: "/" })
    } catch (error) {
      // Error is handled by the auth hooks
      console.error("Login failed:", error)
    }
  }

  return (
    <Box minH="100vh">
      <PublicHeader hideAuthButtons />
      <Container
        as="form"
        onSubmit={handleSubmit(onSubmit)}
        h="calc(100vh - 64px)"
        maxW="sm"
        px={{ base: 4, md: 0 }}
        alignItems="stretch"
        justifyContent="center"
        gap={4}
        centerContent
      >
        <Image
          src={Logo}
          alt="Dory Swim School"
          height="auto"
          maxW={{ base: "200px", md: "xs" }}
          alignSelf="center"
          mb={4}
        />
        <Input
          id="username"
          {...register("username", {
            required: "Username is required",
            pattern: emailPattern,
          })}
          placeholder="Email"
          type="email"
        />
        {errors.username && (
          <Text color="red.500" fontSize="sm">
            {errors.username.message}
          </Text>
        )}
        {error && (
          <Text color="red.500" fontSize="sm">
            {error.message}
          </Text>
        )}
        <Input
          type="password"
          {...register("password", passwordRules())}
          placeholder="Password"
        />
        {errors.password && (
          <Text color="red.500" fontSize="sm">
            {errors.password.message}
          </Text>
        )}
        <RouterLink to="/recover-password" className="main-link">
          Forgot Password?
        </RouterLink>
        <Button
          variant="solid"
          type="submit"
          loading={isSubmitting || isLoading}
          size="md"
          minH="44px"
        >
          Log In
        </Button>
        <Text>
          Don't have an account?{" "}
          <RouterLink to="/signup" className="main-link">
            Sign Up
          </RouterLink>
        </Text>
      </Container>
    </Box>
  )
}
